import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { useLabelFilterStore, useLabelsStore, useSettingsStore } from "../../stores/index.js";
import { orderByIds } from "../../lib/apply-order.js";
import { useI18n } from "../../lib/useI18n.js";
import { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import { Reorder, useDragControls } from "../../lib/motion/index.js";
import { ModeToggle } from "../sidebar/ModeToggle.js";
import { SidebarSection } from "../sidebar/SidebarSection.js";
import { useSidebarReorderProps } from "../sidebar/useSidebarReorderProps.js";

/** Sidebar label filter. A collapsible (persisted) section with an Any/All match
 *  toggle; clicking a label toggles it in the selection, and the rows are
 *  drag-to-reorder (order persists + backed up). */
export function LabelList({ grip, onTitlePointerDown }: { grip?: ReactNode; onTitlePointerDown?: (e: ReactPointerEvent) => void } = {}) {
  const { t } = useI18n();
  const labels = useLabelsStore((s) => s.labels);
  const { selected, mode, toggle, clear, setMode } = useLabelFilterStore();
  const sectionCollapsed = useSettingsStore((s) => s.sidebarCollapsed.labels);
  const setSidebarCollapsed = useSettingsStore((s) => s.setSidebarCollapsed);
  const labelOrder = useSettingsStore((s) => s.labelOrder);
  const setLabelOrder = useSettingsStore((s) => s.setLabelOrder);
  const guard = useDragClickGuard();
  const reorderProps = useSidebarReorderProps();
  if (labels.length === 0) return null;

  const ordered = orderByIds(labels, labelOrder, (l) => l.id);

  return (
    <SidebarSection
      title={t("common.labels")}
      manageTo="/settings/organize"
      grip={grip}
      onTitlePointerDown={onTitlePointerDown}
      collapsed={sectionCollapsed}
      onToggle={() => void setSidebarCollapsed("labels", !sectionCollapsed)}
    >
      <div className="mb-1 flex items-center gap-2 px-2">
        <ModeToggle mode={mode} setMode={setMode} noun={t("sidebar.label")} />
        {selected.length > 0 && (
          <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">{t("sidebar.clear")}</button>
        )}
      </div>
      <Reorder.Group as="div" axis="y" values={ordered.map((l) => l.id)} onReorder={(ids) => void setLabelOrder(ids as string[])}>
        {ordered.map((label) => (
          <LabelItem
            key={label.id}
            label={label}
            active={selected.includes(label.id)}
            guard={guard}
            reorderProps={reorderProps}
            onToggle={() => toggle(label.id)}
          />
        ))}
      </Reorder.Group>
    </SidebarSection>
  );
}

type Label = ReturnType<typeof useLabelsStore.getState>["labels"][number];

function LabelItem({
  label,
  active,
  guard,
  reorderProps,
  onToggle,
}: {
  label: Label;
  active: boolean;
  guard: ReturnType<typeof useDragClickGuard>;
  reorderProps: ReturnType<typeof useSidebarReorderProps>;
  onToggle: () => void;
}) {
  const { t } = useI18n();
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={label.id}
      dragListener={false}
      dragControls={controls}
      onDragStart={guard.onDragStart}
      onDragEnd={guard.onDragEnd}
      {...reorderProps}
    >
      <button
        type="button"
        onClick={() => { if (guard.suppressClick()) return; onToggle(); }}
        aria-pressed={active}
        className={`flex w-full items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent ${active ? "bg-accent font-medium" : ""}`}
        title={active ? t("sidebar.filteringLabel") : t("sidebar.filterByLabel")}
      >
        <span className="h-2.5 w-2.5 shrink-0 rounded-full border" style={{ background: label.color ?? "transparent" }} />
        <span
          title={t("sidebar.dragLabel")}
          onPointerDown={(e) => controls.start(e)}
          className="min-w-0 truncate text-left cursor-grab touch-none active:cursor-grabbing"
        >
          {label.name}
        </span>
        <span aria-hidden className="min-w-0 flex-1" />
      </button>
    </Reorder.Item>
  );
}
